/* eslint-disable import/prefer-default-export */
import InfantIcon from '@/graphics/icons/InfantIcon.vue';
import ToddlerIcon from '@/graphics/icons/ToddlerIcon.vue';
import PreschoolIcon from '@/graphics/icons/PreschoolIcon.vue';
import ElementaryIcon from '@/graphics/icons/ElementaryIcon.vue';
import MiddleSchoolIcon from '@/graphics/icons/MiddleSchoolIcon.vue';
import DogIcon from '@/graphics/icons/DogIcon.vue';
import CatIcon from '@/graphics/icons/CatIcon.vue';
import BirdIcon from '@/graphics/icons/BirdIcon.vue';
import FishIcon from '@/graphics/icons/FishIcon.vue';
import HorseIcon from '@/graphics/icons/HorseIcon.vue';
import FarmAnimalIcon from '@/graphics/icons/FarmAnimalIcon.vue';
import ReptileIcon from '@/graphics/icons/ReptileIcon.vue';
import ExoticPetIcon from '@/graphics/icons/ExoticPetIcon.vue';
import SmallMammalIcon from '@/graphics/icons/SmallMammalIcon.vue';
import OtherIcon from '@/graphics/icons/OtherIcon.vue';

export interface ExperienceDisplay {
  text: string;
  icon?: any;
}

const ageExperienceMap: Record<string, ExperienceDisplay> = {
  FIRST_YEAR: { text: 'Infants (age 0-11 months)', icon: InfantIcon },
  TODDLERS: { text: 'Toddlers (age 1-3)', icon: ToddlerIcon },
  PRESCHOOLERS: { text: 'Preschoolers (age 4-5)', icon: PreschoolIcon },
  GRADE_SCHOOL_AGE: { text: 'Elementary (age 6-10)', icon: ElementaryIcon },
  PRE_TEEN_TEENAGERS: { text: 'Pre-teens & Teens (age 11+)', icon: MiddleSchoolIcon },
};

const petExperienceMap: Record<string, ExperienceDisplay> = {
  DOGS: { text: 'Dogs', icon: DogIcon },
  CATS: { text: 'Cats', icon: CatIcon },
  BIRDS: { text: 'Birds', icon: BirdIcon },
  FISH: { text: 'Fish', icon: FishIcon },
  HORSES: { text: 'Horses', icon: HorseIcon },
  FARM_ANIMALS: { text: 'Farm Animals', icon: FarmAnimalIcon },
  SMALL_MAMMALS: { text: 'Small Mammals', icon: SmallMammalIcon },
  REPTILES_AMPHIBIANS: { text: 'Reptiles/Amphibians', icon: ReptileIcon },
  EXOTIC_PETS: { text: 'Exotic Pets', icon: ExoticPetIcon },
  OTHER: { text: 'Other', icon: OtherIcon },
};

const serviceExperienceMap: Record<string, ExperienceDisplay> = {
  GENERAL_CLEANING: { text: 'General cleaning' },
  SPECIALIZED_CLEANING: { text: 'Specialized cleaning' },
  PREPARE_SNACKS_MEALS: { text: 'Prepare snacks/meals' },
  WASH_DISHES: { text: 'Wash dishes' },
  DO_LAUNDRY: { text: 'Laundry' },
  RUN_ERRANDS: { text: 'Can run errands' },
  GENERAL_MAINTENANCE: { text: 'General maintenance' },
  ORGANIZATION: { text: 'Organization' },
};

const companionCareAgeExperienceMap: Record<string, string> = {
  UNDER_55: 'Under 55 years old',
  FROM_55_TO_64: '55-64 years old',
  FROM_65_TO_74: '65-74 years old',
  FROM_75_TO_84: '75-84 years old',
  OVER_85: 'Over 85 years old',
};

const basicServicesExperienceMap: Record<string, ExperienceDisplay> = {
  CONVERSATION_COMPANIONSHIP: { text: 'Conversation and companionship' },
  SAFETY_SUPERVISION: { text: 'Safety supervision' },
  MEAL_PREPARATION: { text: 'Meal preparation' },
  MEDICATION_REMINDERS: { text: 'Medication reminders' },
  SOCIAL_ACTIVITIES: { text: 'Social activities' },
  LIGHT_HOUSEKEEPING: { text: 'Light housekeeping' },
  EXERCISE_PHYSICAL_ACTIVITIES: { text: 'Exercise/Physical activities' },
  LAUNDRY_LINEN_CHANGES: { text: 'Laundry and linen changes' },
  SHOPPING: { text: 'Shopping' },
  MOBILITY_ASSISTANCE: { text: 'Mobility assistance' },
  BATHING: { text: 'Bathing' },
  DRESSING: { text: 'Dressing' },
  GROOMING: { text: 'Grooming' },
  FEEDING_SPECIAL_DIET: { text: 'Feeding and special diet' },
  TOILETING: { text: 'Toileting' },
};

const personalServicesExperienceMap: Record<string, ExperienceDisplay> = {
  BATHING: { text: 'Bathing' },
  DRESSING: { text: 'Dressing' },
  GROOMING: { text: 'Grooming' },
  FEEDING_SPECIAL_DIET: { text: 'Feeding and special diet' },
  TOILETING: { text: 'Toileting' },
};

const specialNeedsAgeExperienceMap: Record<string, string> = {
  INFANT: 'Infants',
  TODDLER: 'Toddlers',
  YOUTH: 'Youths',
  TEEN: 'Teens',
  ADULT: 'Adults',
  SENIOR: 'Seniors',
};

const specialNeedsCareExperienceMap: Record<string, ExperienceDisplay> = {
  COGNITIVE_DISABILITIES: { text: 'Cognitive disabilities' },
  PHYSICAL_DISABILITIES: { text: 'Physical disabilities' },
  MEDICAL_NEEDS: { text: 'Medical/Special healthcare needs' },
  SOCIAL_EMOTIONAL: { text: 'Social/emotional' },
};

const specialNeedsConditionExperienceMap: Record<string, ExperienceDisplay> = {
  ALZHEIMER: { text: "Alzheimer's" },
  ASPERGERS_SYNDROME: { text: "Asperger's Syndrome" },
  ATTENTION_DEFICIT_AND_HYPERACTIVITY_DISORDER: { text: 'Attention Deficit and Hyperactivity Disorder' },
  ATTENTION_DEFICIT_DISORDER: { text: 'Attention Deficit Disorder' },
  AUTISM: { text: 'Autism' },
  AUTISM_SPECTRUM_DISORDER: { text: 'Autism Spectrum Disorder' },
  CANCER: { text: 'Cancer' },
  CENTRAL_AUDITORY_PROCESSING_DISORDER: { text: 'Central Auditory Processing Disorder' },
  CEREBRAL_PALSY: { text: 'Cerebral Palsy' },
  CYSTIC_FIBROSIS: { text: 'Cystic Fibrosis' },
  DELAYED_SPEECH: { text: 'Delayed Speech' },
  DEVELOPMENTAL_LANGUAGE_DISORDER: { text: 'Developmental Language Disorder' },
  DIABETES: { text: 'Diabetes' },
  DOWN_SYNDROME: { text: 'Down Syndrome' },
  DWARFISM: { text: 'Dwarfism' },
  DYSLEXIA: { text: 'Dyslexia' },
  EPILEPSY: { text: 'Epilepsy' },
  FETAL_ALCOHOL_SYNDROME: { text: 'Fetal Alcohol Syndrome' },
  FRAGILE_X_SYNDROME: { text: 'Fragile X Syndrome' },
  HEARING_DISORDERS: { text: 'Hearing Disorders' },
  HEART_DEFECTS: { text: 'Heart Defects' },
  HYDROCEPHALY: { text: 'Hydrocephaly' },
  JUVENILE_DIABETES: { text: 'Juvenile Diabetes' },
  JUVENILE_RHEUMATOID_ARTHRITIS: { text: 'Juvenile Rheumatoid Arthritis' },
  LEARNING_DISABILITIES: { text: 'Learning Disabilities' },
  MENTAL_ILLNESS: { text: 'Mental Illness' },
  MOBILITY_CHANGES: { text: 'Mobility Changes' },
  MULTIPLE_SCLEROSIS: { text: 'Multiple Sclerosis' },
  MUSCULAR_DYSTROPHY: { text: 'Muscular Dystrophy' },
  PERVASIVE_DEVELOPMENT_DISORDER: { text: 'Pervasive Development Disorder' },
  PREMATURE_NEWBORN: { text: 'Premature Newborn' },
  RETT_SYNDROME: { text: 'Rett Syndrome' },
  SEIZURES: { text: 'Seizures' },
  SENSORY_INTEGRATION_DISORDER: { text: 'Sensory Integration Disorder' },
  SPINAL_CORD_INJURY: { text: 'Spinal Cord Injury' },
  TOURETTE_SYNDROME: { text: 'Tourette Syndrome' },
  VISUAL_IMPAIRMENT: { text: 'Visual Impairment' },
};

const specialNeedsServiceExperienceMap: Record<string, ExperienceDisplay> = {
  ADMINISTERING_MEDICATION: { text: 'Administering Medication' },
  APPLIED_BEHAVIOR_ANALYSIS: { text: 'Applied Behavior Analysis' },
  ASSISTANCE_WITH_TRANSPORTATION: { text: 'Assistance With Transportation' },
  BATHING: { text: 'Bathing' },
  BED_BATHS: { text: 'Bed Baths' },
  BLADDER_AND_BOWEL_ASSISTANCE: { text: 'Bladder and Bowel Assistance' },
  BLOOD_GLUCOSE_TESTING: { text: 'Blood Glucose Testing' },
  DRESSING_ASSISTANCE: { text: 'Dressing Assistance' },
  FEEDING_ASSISTANCE: { text: 'Feeding Assistance' },
  FEEDING_TUBE: { text: 'Feeding Tube' },
  GROOMING_AND_HYGIENE: { text: 'Grooming and Hygiene' },
  INJECTION_ASSISTANCE: { text: 'Injection Assistance' },
  INSULIN_INJECTIONS: { text: 'Insulin Injections' },
  LIFTING_AND_TRANSFERRING: { text: 'Lifting and Transferring' },
  MOBILITY_ASSISTANCE: { text: 'Mobility Assistance' },
  POSITIVE_BEHAVIORAL_SUPPORT: { text: 'Positive Behavioral Support' },
  RECREATIONAL_MUSIC_OR_ART_THERAPY: { text: 'Recreational Music or Art Therapy' },
  REPOSITIONING: { text: 'Repositioning' },
  RESPIRATORY_CARE: { text: 'Respiratory Care' },
  SIGN_LANGUAGE: { text: 'Sign Language' },
  SPECIFIC_MEAL_PREPARATION: { text: 'Specific meal preparation' },
  WILBARGER_BRUSHING_PROTOCOL: { text: 'Wilbarger Brushing Protocol' },
};

export const experienceMap = {
  ageGroups: ageExperienceMap,
  petExperience: petExperienceMap,
  services: serviceExperienceMap,
  companionCareAges: companionCareAgeExperienceMap,
  basicCare: basicServicesExperienceMap,
  personalCare: personalServicesExperienceMap,
  specialNeedsAges: specialNeedsAgeExperienceMap,
  specialNeedsCare: specialNeedsCareExperienceMap,
  specialNeedsConditions: specialNeedsConditionExperienceMap,
  specialNeedsServices: specialNeedsServiceExperienceMap,
};

export const getExperiences = (profileData: Record<string, any>, careType: string): Record<string, any> => {
  switch (careType) {
    case 'babysitter':
    case 'nanny':
      return { ageGroups: profileData.ageGroups };
    case 'petCare':
      return { experienceCaringFor: profileData.experienceCaringFor };
    case 'housekeeping':
      return { servicesExperience: profileData.servicesExperience };
    case 'companionCare':
      return {
        ageGroupsExperience: profileData.ageGroupsExperience,
        basicCareServicesExperience: profileData.basicCareServicesExperience,
        personalCareServicesExperience: profileData.personalCareServicesExperience,
      };
    default:
      return {};
  }
};

export const hasExperiences = (profileData: Record<string, any>, careType: string): boolean => {
  const experiences = getExperiences(profileData, careType);
  return Object.values(experiences).some((value) => Array.isArray(value) && value.length > 0);
};

export const getSpecialNeedsExperiences = (profileData: Record<string, any>): Record<string, any> => {
  return {
    specialNeedsRecipientAgeGroups: profileData.specialNeedsRecipientAgeGroups,
    specialNeedsCareExperience: profileData.specialNeedsCareExperience,
    specialNeedsConditionExperience: profileData.specialNeedsConditionExperience,
    specialNeedsServiceExperience: profileData.specialNeedsServiceExperience,
  };
};

export const hasSpecialNeedsExperiences = (profileData: Record<string, any>): boolean => {
  const experiences = getSpecialNeedsExperiences(profileData);
  return Object.values(experiences).some((value) => Array.isArray(value) && value.length > 0);
};
